import {
  EDITOR_PASTEBOARD_SIZE_PT,
  PHOTO_DRAG_THRESHOLD_SCREEN_PX,
  PHOTO_INI_SCALE_RATE,
  PHOTO_PLACEMENT_DEFAULT_WIDTH_PT,
} from '@/lib/photos/photo-placement-config'
import type { PageSide } from '@/lib/photos/types/page-side'
import type { CanvasPlacementBounds } from './types/canvas-placement-bounds'
import type { PhotoPlacementSize } from './types/photo-placement-size'

/** drag starts after 4px of **screen-space** pointer movement. */
export function meetsPhotoDragThreshold(dxScreen: number, dyScreen: number): boolean {
  return Math.hypot(dxScreen, dyScreen) >= PHOTO_DRAG_THRESHOLD_SCREEN_PX
}

/**
 * Free-drop / new-frame size in points — matches legacy odctrl `IM_INI_SCALE_RATE`:
 * `floor(pixel × 72/300)`. Prefers server/natural pixel dimensions when known.
 */
export function computePhotoPlacementSize(
  pixelWidth?: number,
  pixelHeight?: number
): PhotoPlacementSize {
  const hasPixels =
    typeof pixelWidth === 'number' &&
    typeof pixelHeight === 'number' &&
    pixelWidth > 0 &&
    pixelHeight > 0

  if (!hasPixels) {
    return {
      width: PHOTO_PLACEMENT_DEFAULT_WIDTH_PT,
      height: PHOTO_PLACEMENT_DEFAULT_WIDTH_PT,
    }
  }

  return {
    width: Math.floor(pixelWidth * PHOTO_INI_SCALE_RATE),
    height: Math.floor(pixelHeight * PHOTO_INI_SCALE_RATE),
  }
}

/**
 * Size for the new photo box when continuing after a “Photo Size” warning
 * (same 300 DPI formula as free drop — legacy odctrl parity).
 */
export function computeLegacyReplacementFrameSize(
  naturalWidth?: number,
  naturalHeight?: number
): PhotoPlacementSize {
  return computePhotoPlacementSize(naturalWidth, naturalHeight)
}

/** Top-left position so the shape center sits on (centerX, centerY). */
export function topLeftFromCenter(
  centerX: number,
  centerY: number,
  width: number,
  height: number
): { x: number; y: number } {
  return {
    x: centerX - width / 2,
    y: centerY - height / 2,
  }
}

/**
 * Placement bounds for the full gray pasteboard in spread-relative canvas coordinates.
 * Spread is centered on the pasteboard; coordinates may be negative outside the bleed.
 */
export function getPasteboardPlacementBounds(
  bleedWidth: number,
  bleedHeight: number,
  pasteboardSize = EDITOR_PASTEBOARD_SIZE_PT
): CanvasPlacementBounds {
  const halfBoard = pasteboardSize / 2
  const halfBleedW = bleedWidth / 2
  const halfBleedH = bleedHeight / 2

  return {
    minX: halfBleedW - halfBoard,
    minY: halfBleedH - halfBoard,
    maxX: halfBoard + halfBleedW,
    maxY: halfBoard + halfBleedH,
  }
}

/**
 * Keep the photo center inside the placement bounds; allow edges to overflow visually.
 */
export function clampPhotoCenterToCanvas(
  centerX: number,
  centerY: number,
  width: number,
  height: number,
  bounds: CanvasPlacementBounds
): { centerX: number; centerY: number } {
  const halfW = width / 2
  const halfH = height / 2

  return {
    centerX: Math.max(bounds.minX + halfW, Math.min(bounds.maxX - halfW, centerX)),
    centerY: Math.max(bounds.minY + halfH, Math.min(bounds.maxY - halfH, centerY)),
  }
}

/**
 * Placement bounds for the printable spread (bleed box) in canvas coordinates.
 * Origin is the top-left of the bleed; edges are inclusive for “on spread” checks.
 */
export function getSpreadPlacementBounds(
  bleedWidth: number,
  bleedHeight: number
): CanvasPlacementBounds {
  return {
    minX: 0,
    minY: 0,
    maxX: bleedWidth,
    maxY: bleedHeight,
  }
}

/**
 * True when the axis-aligned rect overlaps the bounds (shared edge counts as on-spread).
 */
export function doesRectIntersectBounds(
  x: number,
  y: number,
  width: number,
  height: number,
  bounds: CanvasPlacementBounds
): boolean {
  if (!(width > 0) || !(height > 0)) return false

  return (
    x <= bounds.maxX && x + width >= bounds.minX && y <= bounds.maxY && y + height >= bounds.minY
  )
}

/** True when a shape’s stored rect intersects the spread bleed (including edge touch). */
export function doesShapeIntersectSpread(
  shape: { x: number; y: number; width: number; height: number },
  bleedWidth: number,
  bleedHeight: number
): boolean {
  if (!(bleedWidth > 0) || !(bleedHeight > 0)) return false

  return doesRectIntersectBounds(
    shape.x,
    shape.y,
    shape.width,
    shape.height,
    getSpreadPlacementBounds(bleedWidth, bleedHeight)
  )
}

/**
 * The pages of a spread a shape touches: `'left'` and/or `'right'`, none when it misses the spread.
 *
 * The spine is the middle of the bleed box. The outer edges count when touched (as
 * `doesShapeIntersectSpread` does), but a shape that only reaches the spine from one side is on
 * that page alone. A single-page spread (a book's first or last page) keeps the full two-page box
 * with one half drawn as pasteboard, so only the page half counts (`'first'` is the right half,
 * `'last'` the left).
 */
export function pageSidesOfShape(
  shape: { x: number; y: number; width: number; height: number },
  bleedWidth: number,
  bleedHeight: number,
  singlePageSide?: 'first' | 'last' | null
): Set<PageSide> {
  const sides = new Set<PageSide>()

  if (!doesShapeIntersectSpread(shape, bleedWidth, bleedHeight)) return sides

  const spineX = Math.floor(bleedWidth / 2)

  if (shape.x < spineX && singlePageSide !== 'first') sides.add('left')
  if (shape.x + shape.width > spineX && singlePageSide !== 'last') sides.add('right')

  return sides
}

export function isCanvasPointInBounds(
  canvasX: number,
  canvasY: number,
  bounds: CanvasPlacementBounds
): boolean {
  return (
    canvasX >= bounds.minX &&
    canvasY >= bounds.minY &&
    canvasX <= bounds.maxX &&
    canvasY <= bounds.maxY
  )
}
